// Single source of truth for reservations-app payment-link credit-card fee recovery.
//
// Fee recovery is currently DISABLED for every provider (Stripe / Omise / 2C2P): all macros
// send feePercent = 0 to /api/reservations/generate-payment-links, so the payable amount equals
// the net amount and nothing is added on top. The macro wording keys off these functions
// returning > 0, so when fees are off the messaging drops all "credit card processing fees"
// references automatically.
//
// ── TO BRING FEES BACK ──
// Flip FEE_RECOVERY_ENABLED to `true`. The original per-provider rates below are restored, and
// every macro re-derives both the charged fee and its wording from these functions — no other
// edit needed in the macro components. (The fallback token defaults in the Components package's
// MacroBindingContext / macro-puck-ssr mirror this disabled state with their own comment.)

export const FEE_RECOVERY_ENABLED = false;

// Original rates, pre-disable. Restored automatically when FEE_RECOVERY_ENABLED = true.
//  - Stripe / Omise: grossed-up so we receive the net — full 1.85%, partial (deposit) 3.65%.
//  - 2C2P: surcharge on top — full 0%; partial 1.65% when the payment is ≤ 50% of the
//    reservation total, otherwise 0%.
const FULL_FEE_NON_2C2P = 1.85;
const PARTIAL_FEE_NON_2C2P = 3.65;
const PARTIAL_FEE_2C2P_LOW_PAYMENT = 1.65;

function is2C2P(provider: string): boolean {
  return provider.toLowerCase().startsWith('2c2p');
}

/** Fee percent for a FULL payment link. */
export function getFullPaymentFeePercent(provider: string): number {
  if (!FEE_RECOVERY_ENABLED) return 0;
  return is2C2P(provider) ? 0 : FULL_FEE_NON_2C2P;
}

/**
 * Fee percent for a PARTIAL (deposit) payment link.
 * `percentOfTotal` is the payment expressed as a percentage (0–100) of the reservation total;
 * only 2C2P uses it (≤ 50% → 1.65%, otherwise 0%). Non-2C2P ignores it.
 */
export function getPartialPaymentFeePercent(provider: string, percentOfTotal: number): number {
  if (!FEE_RECOVERY_ENABLED) return 0;
  if (is2C2P(provider)) {
    return percentOfTotal <= 50 ? PARTIAL_FEE_2C2P_LOW_PAYMENT : 0;
  }
  return PARTIAL_FEE_NON_2C2P;
}
